import { Typography } from "@mui/material";
import React from "react";
import { HosDriverProfileDto } from "../../../api/vehiclesApi";
import {
  Column,
  DataTable,
  makeColumnsSearchable,
} from "../../../components/common/DataTable/DataTable";
import { formatHosTime, formatTableTime } from "../../../utils/dateUtils";
import { getHosDutyStatusColor, getHosDutyStatusLabel } from "../../../utils/statusColors";

interface HosTableProps {
  profiles: HosDriverProfileDto[];
  isLoading: boolean;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  
  // Search functionality
  enableSearch?: boolean;
  onSearchChange?: (filters: Record<string, any>) => void;
}

const HosTable: React.FC<HosTableProps> = ({
  profiles,
  isLoading,
  totalCount,
  hasNextPage,
  isLoadingMore,
  onLoadMore,
  
  // Search functionality
  enableSearch = false,
  onSearchChange,
}) => {
  const columns: Column[] = [
    {
      field: "driverName",
      headerName: "Driver Name",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "dutyStatus",
      headerName: "Status",
      flex: 1,
      renderCell: (value: string, profile: HosDriverProfileDto) => {
        const statusTime =
          value === "DRIVING"
            ? profile.driveTime
            : value === "ON_DUTY"
            ? profile.shiftTime
            : profile.breakTime;

        return (
          <span
            style={{
              color: getHosDutyStatusColor(value),
              fontWeight: "bold",
            }}
          >
            {getHosDutyStatusLabel(value)}: {formatHosTime(statusTime)}
          </span>
        );
      },
    },
    {
      field: "breakTime",
      headerName: "Until Break",
      flex: 1,
      renderCell: (value: number) => formatTableTime(value),
    },
    {
      field: "driveTime",
      headerName: "Drive",
      flex: 1,
      renderCell: (value: number) => formatTableTime(value),
    },
    {
      field: "shiftTime",
      headerName: "Shift",
      flex: 1,
      renderCell: (value: number) => formatTableTime(value),
    },
    {
      field: "cycleTime",
      headerName: "Cycle",
      flex: 1,
      renderCell: (value: number) => formatTableTime(value),
    },
    {
      field: "cycleTomorrowTime",
      headerName: "Cycle Tomorrow",
      flex: 1,
      renderCell: (value: number) => formatTableTime(value),
    },
    {
      field: "lastSyncTime",
      headerName: "Last HOS Sync",
      flex: 1,
      renderCell: (value: string) => new Date(value).toLocaleString(),
    },
  ];

  // Define searchable columns for HOS data
  const searchableColumns = makeColumnsSearchable(
    columns,
    ['driverName', 'dutyStatus'],
    {
      driverName: 'string',
      dutyStatus: 'string'
    }
  );

  return (
    <DataTable
      columns={columns}
      items={profiles}
      isLoading={isLoading}
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      onLoadMore={onLoadMore}
      
      // Search functionality
      enableSearch={enableSearch}
      searchableColumns={searchableColumns}
      onSearchChange={onSearchChange}
    />
  );
};

export default HosTable;
